import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Receipt, Plus, Search, Loader2, Trash2, Send, CreditCard, Copy, FileDown, Download, Bell, Zap } from 'lucide-react';
import { formatCurrency, formatDateShort, formatStatutFiscal, getStatutFiscalColor, NATURE_OPERATION_LABELS } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  brouillon: 'bg-slate-100 text-slate-700',
  envoyee: 'bg-blue-100 text-blue-700',
  payee: 'bg-green-100 text-green-700',
  partiellement_payee: 'bg-amber-100 text-amber-700',
  en_retard: 'bg-red-100 text-red-700',
  annulee: 'bg-slate-100 text-slate-500',
};
const STATUS_LABELS = {
  brouillon: 'Brouillon', envoyee: 'Envoyée', payee: 'Payée',
  partiellement_payee: 'Part. payée', en_retard: 'En retard', annulee: 'Annulée',
};

const round2 = (n) => Math.round(n * 100) / 100;

export default function FacturesClients() {
  const navigate = useNavigate();
  const [factures, setFactures] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(null);
  const [payOpen, setPayOpen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendMsg, setSendMsg] = useState('');
  const [payForm, setPayForm] = useState({ montant_paye: 0, date_paiement: new Date().toISOString().split('T')[0], mode_paiement: 'virement', reference_paiement: '' });
  const [stats, setStats] = useState(null);
  const [clientFilter, setClientFilter] = useState('all');
  const [form, setForm] = useState({
    client_id: '', date_facture: new Date().toISOString().split('T')[0], date_echeance: '', objet: '',
    lignes: [{ prestation_id: '', description: '', descriptif: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20, categorie_tva: 'S' }],
    conditions_paiement: '30 jours', notes: '',
    // E-invoicing (réforme sept. 2026)
    nature_operation: 'prestation_services',
    reference_commande: '',
  });

  const [prestations, setPrestations] = useState([]);

  const fetchAll = async () => {
    try {
      const params = { limit: 500 };
      if (tab === 'avoirs') {
        params.type_document = 'avoir';
      } else {
        params.type_document = 'facture';
        if (tab !== 'all') params.statut = tab;
      }
      if (search) params.search = search;
      if (clientFilter && clientFilter !== 'all') params.client_id = clientFilter;
      const [fRes, cRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/factures-clients`, { params }),
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/prestations`).catch(() => ({ data: [] })),
      ]);
      setFactures(fRes.data);
      setClients(cRes.data);
      setPrestations(pRes.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchAll(); }, [tab, search, clientFilter]);

  useEffect(() => {
    axios.get(`${API_URL}/factures-clients/stats`).then(r => setStats(r.data)).catch(() => {});
  }, [factures]);

  const calcTotals = (lignes) => {
    let ht = 0, tva = 0;
    lignes.forEach(l => { const lht = (l.quantite || 0) * (l.prix_unitaire_ht || 0); ht += lht; tva += lht * (l.taux_tva || 20) / 100; });
    return { ht: Math.round(ht * 100) / 100, tva: Math.round(tva * 100) / 100, ttc: Math.round((ht + tva) * 100) / 100 };
  };

  const addPrestationEmptyLine = () => setForm({ ...form, lignes: [...form.lignes, { prestation_id: '', description: '', descriptif: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20, categorie_tva: 'S' }] });
  const removeLine = (i) => setForm({ ...form, lignes: form.lignes.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, value) => { const l = [...form.lignes]; l[i] = { ...l[i], [field]: value }; setForm({ ...form, lignes: l }); };
  const totals = calcTotals(form.lignes);

  const handleCreate = async () => {
    if (!form.client_id || !form.date_facture || !form.date_echeance) { toast.error('Champs obligatoires manquants'); return; }
    setSaving(true);
    try {
      // Merge description + descriptif for each line
      const payload = {
        ...form,
        lignes: form.lignes.map(l => ({
          description: l.descriptif ? `${l.description} — ${l.descriptif}` : l.description,
          quantite: l.quantite,
          prix_unitaire_ht: l.prix_unitaire_ht,
          taux_tva: l.taux_tva,
          categorie_tva: l.categorie_tva || 'S',
        })),
      };
      await axios.post(`${API_URL}/factures-clients`, payload);
      toast.success('Facture créée');
      setCreateOpen(false);
      setForm({ client_id: '', date_facture: new Date().toISOString().split('T')[0], date_echeance: '', objet: '', lignes: [{ description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20, categorie_tva: 'S' }], conditions_paiement: '30 jours', notes: '', nature_operation: 'prestation_services', reference_commande: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleSend = async () => {
    if (!sendEmail || !sendOpen) return;
    try {
      await axios.post(`${API_URL}/factures-clients/${sendOpen}/envoyer`, { email_destinataire: sendEmail, message: sendMsg });
      toast.success('Facture envoyée');
      setSendOpen(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
  };

  const handlePay = async () => {
    if (!payOpen) return;
    try {
      await axios.post(`${API_URL}/factures-clients/${payOpen}/paiement`, payForm);
      toast.success('Paiement enregistré');
      setPayOpen(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/factures-clients/${id}/dupliquer`);
      toast.success(`Facture ${res.data.numero} dupliquée`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAvoir = async (id) => {
    if (!confirm('Créer un avoir sur cette facture ?')) return;
    try {
      const res = await axios.post(`${API_URL}/factures-clients/${id}/avoir`);
      toast.success(`Avoir ${res.data.numero} créé`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadPDF = async (id, numero, download = true) => {
    try {
      const res = await axios.get(`${API_URL}/factures-clients/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a'); a.href = url; a.download = `facture_${numero}.pdf`; a.click();
      } else {
        window.open(url, '_blank');
      }
    } catch (e) { toast.error('Erreur génération PDF'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce brouillon ?')) return;
    try {
      await axios.delete(`${API_URL}/factures-clients/${id}`);
      toast.success('Supprimée');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleRelance = async (id) => {
    try {
      await axios.post(`${API_URL}/relances/${id}/envoyer`);
      toast.success('Relance envoyée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white"><Receipt className="w-6 h-6 inline mr-2" />Factures clients</h1>
          <p className="text-slate-500">{factures.length} facture(s)</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle facture
        </Button>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-5 gap-3">
          <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-slate-500">Total</p></CardContent></Card>
          <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-[#0F5156]">{formatCurrency(stats.total_facture)}</p><p className="text-xs text-slate-500">Facturé</p></CardContent></Card>
          <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_paye)}</p><p className="text-xs text-slate-500">Payé</p></CardContent></Card>
          <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.total_impaye)}</p><p className="text-xs text-slate-500">Impayé</p></CardContent></Card>
          <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-red-600">{stats.en_retard}</p><p className="text-xs text-slate-500">En retard</p></CardContent></Card>
          {(stats.deposees_pa > 0 || stats.encaissees_pa > 0) && (
            <Card className="col-span-5 border-blue-200 dark:border-blue-800"><CardContent className="pt-4 flex items-center gap-6">
              <Zap className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">E-Invoicing</span>
              <Badge className="bg-blue-100 text-blue-700">{stats.deposees_pa || 0} déposée(s) PA</Badge>
              <Badge className="bg-emerald-100 text-emerald-700">{stats.encaissees_pa || 0} encaissée(s)</Badge>
            </CardContent></Card>
          )}
        </div>
      )}

      <div className="flex gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="Rechercher..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Client" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les clients</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">Toutes</TabsTrigger>
          <TabsTrigger value="brouillon">Brouillons</TabsTrigger>
          <TabsTrigger value="envoyee">Envoyées</TabsTrigger>
          <TabsTrigger value="payee">Payées</TabsTrigger>
          <TabsTrigger value="en_retard">En retard</TabsTrigger>
          <TabsTrigger value="avoirs">Avoirs</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead>Échéance</TableHead><TableHead className="text-right">Montant TTC</TableHead><TableHead className="text-right">Payé</TableHead><TableHead>Statut</TableHead><TableHead>E-Invoicing</TableHead><TableHead className="w-48">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {factures.map(f => {
                const restant = round2(f.montant_ttc - (f.montant_paye || 0));
                return (
                <TableRow key={f.id}>
                  <TableCell className="font-mono text-sm font-medium">{f.numero}</TableCell>
                  <TableCell className="font-medium">{f.client_name || '-'}</TableCell>
                  <TableCell className="text-sm">{formatDateShort(f.date_facture)}</TableCell>
                  <TableCell className="text-sm">{formatDateShort(f.date_echeance)}</TableCell>
                  <TableCell className="text-right font-mono font-medium">{formatCurrency(f.montant_ttc)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{f.montant_paye > 0 ? <span className="text-green-600">{formatCurrency(f.montant_paye)}</span> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell><Badge className={STATUS_COLORS[f.statut] || ''}>{STATUS_LABELS[f.statut] || f.statut}</Badge></TableCell>
                  <TableCell>{f.statut_fiscal ? <Badge className={getStatutFiscalColor(f.statut_fiscal)}>{formatStatutFiscal(f.statut_fiscal)}</Badge> : <span className="text-slate-300 text-xs">—</span>}</TableCell>
                  <TableCell>
                    <div className="flex gap-0.5">
                      {/* PDF: click = view, right-click context menu still works for save */}
                      <Button variant="ghost" size="sm" title="Voir le PDF" onClick={() => handleDownloadPDF(f.id, f.numero, false)}><Download className="w-3 h-3" /></Button>
                      {/* Envoyer */}
                      {f.statut === 'brouillon' && (
                        <Button variant="ghost" size="sm" title="Envoyer" onClick={() => { setSendEmail(''); setSendMsg(''); setSendOpen(f.id); const cl = clients.find(c => c.id === f.client_id); if (cl?.email) setSendEmail(cl.email); }}>
                          <Send className="w-3 h-3" />
                        </Button>
                      )}
                      {/* Paiement */}
                      {['envoyee', 'partiellement_payee', 'en_retard'].includes(f.statut) && (
                        <Button variant="ghost" size="sm" title="Paiement" onClick={() => { setPayForm({ montant_paye: restant, date_paiement: new Date().toISOString().split('T')[0], mode_paiement: 'virement', reference_paiement: '' }); setPayOpen(f.id); }}>
                          <CreditCard className="w-3 h-3" />
                        </Button>
                      )}
                      {/* Relance */}
                      {['envoyee', 'partiellement_payee', 'en_retard'].includes(f.statut) && (
                        <Button variant="ghost" size="sm" title="Relancer" onClick={() => handleRelance(f.id)}><Bell className="w-3 h-3 text-amber-500" /></Button>
                      )}
                      {/* Dupliquer */}
                      <Button variant="ghost" size="sm" title="Dupliquer" onClick={() => handleDuplicate(f.id)}><Copy className="w-3 h-3" /></Button>
                      {/* Avoir — only if no avoir exists yet for this invoice */}
                      {f.statut !== 'brouillon' && f.statut !== 'annulee' && f.type_document !== 'avoir' && !factures.some(a => a.avoir_reference_id === f.id) && (
                        <Button variant="ghost" size="sm" title="Créer un avoir" onClick={() => handleAvoir(f.id)}><FileDown className="w-3 h-3 text-red-400" /></Button>
                      )}
                      {/* Supprimer brouillon */}
                      {f.statut === 'brouillon' && (
                        <Button variant="ghost" size="sm" title="Supprimer" onClick={() => handleDelete(f.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {factures.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-500">Aucune facture client</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle facture client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Client *</Label>
                <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Objet</Label><Input value={form.objet} onChange={e => setForm({...form, objet: e.target.value})} /></div>
              <div><Label>Date facture *</Label><Input type="date" value={form.date_facture} onChange={e => setForm({...form, date_facture: e.target.value})} /></div>
              <div><Label>Date échéance *</Label><Input type="date" value={form.date_echeance} onChange={e => setForm({...form, date_echeance: e.target.value})} /></div>
            </div>
            {/* Champs e-invoicing obligatoires — réforme sept. 2026 */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="col-span-2 flex items-center gap-2 mb-1"><Zap className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-700 dark:text-blue-300">E-Invoicing (obligatoire sept. 2026)</span></div>
              <div><Label>Nature de l'opération *</Label>
                <Select value={form.nature_operation} onValueChange={v => setForm({...form, nature_operation: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(NATURE_OPERATION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Réf. bon de commande</Label><Input value={form.reference_commande} onChange={e => setForm({...form, reference_commande: e.target.value})} placeholder="BC-2026-..." /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Lignes</Label>
                <Button variant="outline" size="sm" onClick={addPrestationEmptyLine}><Plus className="w-3 h-3 mr-1" /> Ajouter une ligne</Button>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Prestation</TableHead><TableHead>Descriptif</TableHead><TableHead className="w-20">Qté</TableHead><TableHead className="w-28">PU HT</TableHead><TableHead className="w-20">TVA %</TableHead><TableHead className="w-28">Total HT</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                <TableBody>{form.lignes.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Select value={l.prestation_id || '__custom'} onValueChange={v => {
                        if (v === '__custom') return;
                        const p = prestations.find(pr => pr.id === v);
                        if (p) {
                          const ls = [...form.lignes];
                          ls[i] = { ...ls[i], prestation_id: p.id, description: p.designation, prix_unitaire_ht: p.prix_unitaire_ht, taux_tva: p.taux_tva };
                          setForm({ ...form, lignes: ls });
                        }
                      }}>
                        <SelectTrigger className="text-xs"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                        <SelectContent>
                          {prestations.map(p => <SelectItem key={p.id} value={p.id}>{p.reference} — {p.designation} ({formatCurrency(p.prix_unitaire_ht)})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input value={l.descriptif || ''} onChange={e => updateLine(i, 'descriptif', e.target.value)} placeholder="Détails complémentaires..." className="text-xs" /></TableCell>
                    <TableCell><Input type="number" value={l.quantite} onChange={e => updateLine(i, 'quantite', parseFloat(e.target.value) || 0)} /></TableCell>
                    <TableCell><Input type="number" step="0.01" value={l.prix_unitaire_ht} onChange={e => updateLine(i, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)} /></TableCell>
                    <TableCell><Input type="number" value={l.taux_tva} onChange={e => updateLine(i, 'taux_tva', parseFloat(e.target.value) || 0)} /></TableCell>
                    <TableCell className="font-mono">{formatCurrency(l.quantite * l.prix_unitaire_ht)}</TableCell>
                    <TableCell>{form.lignes.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
              <div className="flex justify-end mt-2 text-sm space-y-1 text-right">
                <div className="space-y-1">
                  <div>HT : <strong>{formatCurrency(totals.ht)}</strong></div>
                  <div>TVA : <strong>{formatCurrency(totals.tva)}</strong></div>
                  <div className="text-lg">TTC : <strong className="text-[#0F5156]">{formatCurrency(totals.ttc)}</strong></div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer la facture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={!!sendOpen} onOpenChange={() => setSendOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle><Send className="w-5 h-5 inline mr-2" />Envoyer la facture</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Email destinataire *</Label><Input type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)} /></div>
            <div><Label>Message (optionnel)</Label><Textarea value={sendMsg} onChange={e => setSendMsg(e.target.value)} placeholder="Message personnalisé..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(null)}>Annuler</Button>
            <Button onClick={handleSend} className="bg-[#0F5156] hover:bg-[#0d464a]"><Send className="w-4 h-4 mr-2" />Envoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Dialog */}
      <Dialog open={!!payOpen} onOpenChange={() => setPayOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle><CreditCard className="w-5 h-5 inline mr-2" />Enregistrer un paiement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Montant *</Label><Input type="number" step="0.01" value={payForm.montant_paye} onChange={e => setPayForm({...payForm, montant_paye: parseFloat(e.target.value) || 0})} /></div>
            <div><Label>Date *</Label><Input type="date" value={payForm.date_paiement} onChange={e => setPayForm({...payForm, date_paiement: e.target.value})} /></div>
            <div><Label>Mode de paiement</Label>
              <Select value={payForm.mode_paiement} onValueChange={v => setPayForm({...payForm, mode_paiement: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="cb">Carte bancaire</SelectItem>
                  <SelectItem value="prelevement">Prélèvement</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Référence</Label><Input value={payForm.reference_paiement} onChange={e => setPayForm({...payForm, reference_paiement: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(null)}>Annuler</Button>
            <Button onClick={handlePay} className="bg-[#0F5156] hover:bg-[#0d464a]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
